import { useState, ChangeEvent } from 'react';
import { useQuery } from '@apollo/client';

import usePagination from '@hooks/usePagination';
import { memberStatusTableHeader } from '@lib/data';
import { SEE_USER_LIST } from '@graphql/queries';
import { Query, User } from '@graphql/types';

import { FilterBox, Contents } from '@components/styled/StyledComponents';
import Headline from '@components/layout/Headline';
import StyledSelect from '@components/styled/StyledSelect';
import TableHeader from '@components/share/Table/TableHeader';
import { TableBody, EmptyTable } from '@components/share/Table/table.style';
import MemberStatusTableRow from '@components/feature/Member/Status/MemberStatusTableRow';
import Pagination from '@components/layout/Pagination';
import Loader from '@components/share/Loader';

const MemberStatus = () => {
    const [searchTerm, setSearchTerm] = useState('');
    // 페이지네이션 관련 상태
    const [take, setTake] = useState(10);
    const [preCursor, setPreCursor] = useState(0);
    const [nextCursor, setNextCursor] = useState(0);
    const [cursorOption, setCursorOption] = useState<boolean | null>(null);
    const [filter, setFilter] = useState({ order: 1 });

    const { data, loading } = useQuery<Pick<Query, 'seeUserList'>>(
        SEE_USER_LIST,
        {
            variables: {
                orderBy: +filter.order,
                take,
                preCursor,
                nextCursor,
                cursorOption,
                searchTerm
            },
            onError: (err) => {
                console.error(err);
            }
        }
    );

    const userList = data?.seeUserList?.userList ?? [];
    const totalLength = data?.seeUserList?.totalLength ?? 0;

    const onFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilter((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    // 페이지네이션
    const {
        pageIndex,
        setPageIndex,
        pageGroupLength,
        prevPageHandler,
        nextPageHandler,
        prevPageGroupHandler,
        nextPageGroupHandler,
        resetPaginationHandler,
        disabled
    } = usePagination(
        take,
        totalLength,
        setPreCursor,
        setNextCursor,
        setCursorOption,
        userList,
        'userId'
    );

    return (
        <Contents>
            <Headline
                title="회원별 현황"
                subTitle="펫이닷 회원 현황 및 관리"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchCount={totalLength}
                SEARCH
            />
            {/*  필터 영역 */}
            <FilterBox>
                <StyledSelect
                    title="정렬"
                    name="order"
                    value={filter.order}
                    onChange={onFilterChange}
                    options={[
                        { title: '내림차순', value: 1 },
                        { title: '오름차순', value: 2 }
                    ]}
                    DATA_VALUE
                />
            </FilterBox>
            <TableHeader header={memberStatusTableHeader} />

            <TableBody $minHeight={500}>
                {loading && <Loader />}
                {totalLength === 0 ? (
                    <EmptyTable>회원 현황이 없습니다.</EmptyTable>
                ) : (
                    userList
                        .slice(pageIndex, pageIndex + take)
                        .map((data, idx) => (
                            <MemberStatusTableRow
                                key={`${idx}-member-status-list`}
                                header={memberStatusTableHeader}
                                data={data as User}
                            />
                        ))
                )}
            </TableBody>

            {totalLength > 0 && (
                <Pagination
                    take={take}
                    setTake={setTake}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    pageGroupLength={pageGroupLength}
                    totalLength={totalLength}
                    prevPageHandler={prevPageHandler}
                    nextPageHandler={nextPageHandler}
                    prevPageGroupHandler={prevPageGroupHandler}
                    nextPageGroupHandler={nextPageGroupHandler}
                    resetPaginationHandler={resetPaginationHandler}
                    disabled={disabled}
                />
            )}
        </Contents>
    );
};

export default MemberStatus;
